<template>
  <div class="page-container animate-fade-in-up">
    <PageHeader icon="el-icon-setting" title="调研题目配置" subtitle="配置员工离职调研问卷的题目内容，支持导入导出" />

    <!-- 工具栏 -->
    <div class="toolbar">
      <el-input v-model="search" placeholder="搜索题目关键词" prefix-icon="el-icon-search" clearable style="width: 260px;" />
      <div style="margin-left: auto; display: flex; gap: 12px;">
        <el-button icon="el-icon-download" class="btn-click" @click="downloadTemplate">下载模板</el-button>
        <el-button icon="el-icon-upload2" class="btn-click" @click="openImport">导入题目</el-button>
        <el-button icon="el-icon-download" class="btn-click" @click="exportQuestions">导出题目</el-button>
        <el-button icon="el-icon-view" class="btn-click" @click="openSurveyPreview">预览问卷</el-button>
        <el-button type="primary" icon="el-icon-plus" class="btn-click" @click="openDialog()">新增题目</el-button>
      </div>
    </div>

    <!-- 表格 -->
    <div class="table-wrapper">
      <el-table :data="pagedQuestions" stripe class="data-table">
        <el-table-column type="index" label="序号" width="60" />
        <el-table-column prop="question_text" label="题目内容" min-width="240" show-overflow-tooltip />
        <el-table-column label="题型" width="100">
          <template slot-scope="scope">
            <el-tag size="mini" :type="typeTagType(scope.row.question_type)">{{ typeLabel(scope.row.question_type) }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column label="是否必填" width="80">
          <template slot-scope="scope">
            <el-tag :type="scope.row.required ? 'success' : 'info'" size="mini">{{ scope.row.required ? '是' : '否' }}</el-tag>
          </template>
        </el-table-column>
        <el-table-column prop="sort_order" label="排序" width="70" />
        <el-table-column label="选项预览" min-width="200" show-overflow-tooltip>
          <template slot-scope="scope">
            <span v-if="scope.row.options && scope.row.options.length">{{ scope.row.options.join('、') }}</span>
            <span v-else style="color: var(--color-text-muted);">—</span>
          </template>
        </el-table-column>
        <el-table-column label="操作" width="160">
          <template slot-scope="scope">
            <el-button type="text" class="action-edit" @click="openDialog(scope.row)">编辑</el-button>
            <el-button type="text" @click="previewQuestion(scope.row)">预览</el-button>
            <el-button type="text" class="action-disable" @click="remove(scope.row)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      <div class="pagination-bar">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :total="filteredQuestions.length"
          :page-size="pageSize"
          :current-page="page"
          :page-sizes="[10, 20, 50]"
          @size-change="handleSizeChange"
          @current-change="handlePageChange"
        />
      </div>
    </div>

    <!-- 新增/编辑弹窗 -->
    <el-dialog :title="dialog.isEdit ? '编辑题目' : '新增题目'" :visible.sync="dialog.visible" width="560px" :append-to-body="true">
      <el-form :model="dialog.form" label-width="100px">
        <el-form-item label="题目内容" required>
          <el-input type="textarea" v-model="dialog.form.question_text" :rows="2" placeholder="输入题目内容" :maxlength="500" show-word-limit />
        </el-form-item>
        <el-form-item label="题目类型" required>
          <el-select v-model="dialog.form.question_type" placeholder="选择题型" style="width: 100%;" @change="onTypeChange">
            <el-option label="单选题" value="single_choice" />
            <el-option label="多选题" value="multiple_choice" />
            <el-option label="填空题" value="fill_blank" />
          </el-select>
        </el-form-item>
        <el-form-item label="是否必填">
          <el-switch v-model="dialog.form.required" :active-value="1" :inactive-value="0" />
        </el-form-item>
        <el-form-item label="排序序号">
          <el-input-number v-model="dialog.form.sort_order" :min="0" />
        </el-form-item>

        <!-- 选择题特有 -->
        <template v-if="dialog.form.question_type !== 'fill_blank'">
          <el-form-item label="最多选几项" v-if="dialog.form.question_type === 'multiple_choice'">
            <el-input-number v-model="dialog.form.max_select" :min="1" placeholder="不填则无限制" />
          </el-form-item>
          <el-form-item label="选项列表" required>
            <div v-for="(opt, i) in dialog.form.options" :key="i" class="option-row">
              <el-input v-model="dialog.form.options[i]" size="small" placeholder="选项内容" style="flex: 1;" />
              <el-button type="text" style="color: var(--color-high);" @click="removeOption(i)">删除</el-button>
            </div>
            <el-button type="text" icon="el-icon-plus" @click="addOption">添加选项</el-button>
          </el-form-item>
        </template>
      </el-form>
      <span slot="footer">
        <el-button @click="dialog.visible = false">取消</el-button>
        <el-button type="primary" :loading="saving" @click="save">保存</el-button>
      </span>
    </el-dialog>

    <!-- 题目预览弹窗 -->
    <el-dialog title="题目预览" :visible.sync="previewVisible" width="600px" :append-to-body="true">
      <div v-if="previewRow" class="preview-content">
        <div class="preview-title">
          <span class="preview-number">{{ previewRow.sort_order || 1 }}</span>
          <span>{{ previewRow.question_text }}</span>
          <span v-if="previewRow.required" class="preview-required">*</span>
          <span v-if="previewRow.question_type === 'multiple_choice' && previewRow.max_select" class="preview-limit">最多选{{ previewRow.max_select }}项</span>
        </div>
        <div class="preview-type">题型：{{ typeLabel(previewRow.question_type) }} | {{ previewRow.required ? '必填' : '选填' }}</div>
        <div v-if="previewRow.question_type !== 'fill_blank'" class="preview-options">
          <div v-for="(opt, i) in previewRow.options" :key="i" class="preview-option">
            <span v-if="previewRow.question_type === 'single_choice'" class="preview-radio"></span>
            <span v-else class="preview-checkbox"></span>
            <span>{{ opt }}</span>
          </div>
        </div>
        <div v-else class="preview-fill">
          <div class="preview-fill-placeholder"></div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="previewVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 问卷预览弹窗 -->
    <el-dialog title="问卷预览" :visible.sync="surveyPreviewVisible" width="640px" :append-to-body="true">
      <div class="survey-preview">
        <div class="survey-preview-header">
          <h3 class="survey-preview-title">2026年度骨干人才保留 — 离职调研问卷</h3>
          <p class="survey-preview-subtitle">共 {{ filteredQuestions.length }} 道题目，请根据实际情况填写</p>
        </div>
        <div class="survey-preview-list">
          <div v-for="(q, index) in filteredQuestions" :key="q.question_id" class="survey-preview-item">
            <div class="survey-preview-question">
              <span class="survey-preview-number">{{ index + 1 }}</span>
              <span class="survey-preview-text">{{ q.question_text }}</span>
              <span v-if="q.required" class="survey-preview-required">*</span>
              <span v-if="q.question_type === 'multiple_choice' && q.max_select" class="survey-preview-limit">最多选{{ q.max_select }}项</span>
            </div>
            <div v-if="q.question_type !== 'fill_blank'" class="survey-preview-options">
              <div v-for="(opt, i) in q.options" :key="i" class="survey-preview-option">
                <span v-if="q.question_type === 'single_choice'" class="survey-preview-radio"></span>
                <span v-else class="survey-preview-checkbox"></span>
                <span>{{ opt }}</span>
              </div>
            </div>
            <div v-else class="survey-preview-fill">
              <div class="survey-preview-fill-placeholder"></div>
            </div>
          </div>
        </div>
      </div>
      <span slot="footer">
        <el-button @click="surveyPreviewVisible = false">关闭</el-button>
      </span>
    </el-dialog>

    <!-- 导入弹窗 -->
    <el-dialog title="导入题目" :visible.sync="importVisible" width="560px" :append-to-body="true">
      <div class="import-area">
        <p class="import-tip">
          <i class="el-icon-info" style="color: var(--color-primary);"></i>
          请上传 Excel 模板文件。支持 .xlsx/.xls，也兼容旧 CSV；题目数据请填写在“题目导入”工作表。
        </p>
        <el-upload
          ref="uploadRef"
          action=""
          :auto-upload="false"
          :on-change="handleFileChange"
          :limit="1"
          accept=".xlsx,.xls,.csv"
          drag
          class="upload-box"
        >
          <i class="el-icon-upload"></i>
          <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
        </el-upload>
      </div>
      <span slot="footer">
        <el-button @click="importVisible = false">关闭</el-button>
        <el-button type="primary" :loading="importing" @click="confirmImport">确认导入</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<script>
import PageHeader from '../../components/PageHeader.vue'
import * as XLSX from 'xlsx'

export default {
  name: 'FeedbackSurveyManage',
  components: { PageHeader },
  data() {
    return {
      search: '',
      page: 1,
      pageSize: 10,
      taskId: null,
      dialog: {
        visible: false,
        isEdit: false,
        form: {
          question_id: null,
          task_id: null,
          question_text: '',
          question_type: 'single_choice',
          options: [],
          required: 1,
          sort_order: 0,
          max_select: null
        }
      },
      importVisible: false,
      importContent: '',
      previewVisible: false,
      previewRow: null,
      surveyPreviewVisible: false,
      saving: false,
      importing: false
    }
  },
  computed: {
    questions() {
      return this.$store.state.surveys.questions || []
    },
    filteredQuestions() {
      let data = [...this.questions]
      if (this.search) {
        const k = this.search.toLowerCase()
        data = data.filter(q => (q.question_text || '').toLowerCase().includes(k))
      }
      return data.sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0))
    },
    pagedQuestions() {
      const start = (this.page - 1) * this.pageSize
      return this.filteredQuestions.slice(start, start + this.pageSize)
    }
  },
  watch: {
    '$route.params.taskId'(newVal, oldVal) {
      if (newVal !== oldVal) {
        this.taskId = newVal ? parseInt(newVal, 10) : null
        this.loadQuestions()
      }
    }
  },
  created() {
    const taskId = this.$route.params.taskId
    this.taskId = taskId ? parseInt(taskId, 10) : null
    this.loadQuestions()
  },
  methods: {
    async loadQuestions() {
      try {
        const params = this.taskId ? { task_id: this.taskId } : {}
        const res = await this.$api.fetchSurveyQuestions(params)
        if (res.code === 200) {
          this.$store.commit('SET_SURVEY_QUESTIONS', res.data || [])
        }
      } catch (e) {
        console.error('加载题目失败', e)
      }
    },
    typeLabel(type) {
      const map = { single_choice: '单选题', multiple_choice: '多选题', fill_blank: '填空题' }
      return map[type] || type
    },
    typeTagType(type) {
      const map = { single_choice: 'primary', multiple_choice: 'warning', fill_blank: 'info' }
      return map[type] || ''
    },
    handleSizeChange(size) { this.pageSize = size; this.page = 1 },
    handlePageChange(page) { this.page = page },
    openDialog(row) {
      if (row) {
        this.dialog.isEdit = true
        this.dialog.form = {
          question_id: row.question_id,
          task_id: row.task_id || this.taskId,
          question_text: row.question_text,
          question_type: row.question_type,
          options: [...(row.options || [])],
          required: row.is_required !== undefined ? row.is_required : row.required,
          sort_order: row.sort_order || 0,
          max_select: row.max_select || null
        }
      } else {
        this.dialog.isEdit = false
        this.dialog.form = {
          question_id: null,
          task_id: this.taskId,
          question_text: '',
          question_type: 'single_choice',
          options: [''],
          required: 1,
          sort_order: this.questions.length + 1,
          max_select: null
        }
      }
      this.dialog.visible = true
    },
    onTypeChange() {
      if (this.dialog.form.question_type === 'fill_blank') {
        this.dialog.form.options = []
        this.dialog.form.max_select = null
      } else if (!this.dialog.form.options.length) {
        this.dialog.form.options = ['']
      }
    },
    addOption() {
      this.dialog.form.options.push('')
    },
    removeOption(i) {
      this.dialog.form.options.splice(i, 1)
    },
    async save() {
      this.saving = true
      const form = { ...this.dialog.form }
      if (!form.question_text.trim()) {
        this.$message.error('题目内容不能为空')
        this.saving = false
        return
      }
      if (form.question_type !== 'fill_blank' && form.options.filter(o => o.trim()).length === 0) {
        this.$message.error('选择题至少需要一个选项')
        this.saving = false
        return
      }
      // 过滤空选项
      form.options = form.options.filter(o => o.trim())
      // 补 task_id，required 改名为 is_required
      form.task_id = this.taskId
      form.is_required = form.required
      delete form.required
      try {
        if (this.dialog.isEdit) {
          await this.$api.updateSurveyQuestion(form.question_id, form)
          this.$store.commit('UPDATE_SURVEY_QUESTION', { ...form, required: form.is_required })
          this.$message.success('题目已更新')
        } else {
          const res = await this.$api.createSurveyQuestion(form)
          this.$store.commit('ADD_SURVEY_QUESTION', { ...form, question_id: res.data.question_id, required: form.is_required })
          this.$message.success('题目已新增')
        }
        this.dialog.visible = false
      } catch (e) {
        this.$message.error('保存失败')
      } finally {
        this.saving = false
      }
    },
    async remove(row) {
      try {
        await this.$confirm('确定删除此题目？', '提示', { type: 'warning' })
        await this.$api.deleteSurveyQuestion(row.question_id)
        this.$store.commit('DELETE_SURVEY_QUESTION', row.question_id)
        this.$message.success('已删除')
      } catch (e) {}
    },
    downloadTemplate() {
      const headers = ['题目内容', '题型', '是否必填', '排序', '最多选几项', '选项1', '选项2', '选项3', '选项4', '选项5', '选项6', '选项7']
      const questionRows = [
        headers,
        ['您考虑离职的最主要原因是什么？', 'multiple_choice', '是', 1, 3, '薪酬福利不符合预期', '职业发展受限', '工作压力过大', '管理方式不适应', '家庭/个人原因', '已找到更好的外部机会', '其他'],
        ['您对目前薪酬福利的整体满意度如何？', 'single_choice', '是', 2, '', '非常满意', '比较满意', '一般', '不太满意', '非常不满意', '', ''],
        ['您是否认为公司提供了足够的职业发展空间？', 'single_choice', '是', 3, '', '完全足够', '基本足够', '一般', '不太足够', '完全没有', '', ''],
        ['您对直属上级的管理方式是否认可？', 'single_choice', '是', 4, '', '非常认可', '比较认可', '一般', '不太认可', '完全不认可', '', ''],
        ['如果公司对您的核心诉求做出改善，您是否愿意重新考虑留下？', 'single_choice', '是', 5, '', '愿意', '需要看具体情况', '不愿意', '', '', '', ''],
        ['您对公司有哪些具体的改进建议？', 'fill_blank', '否', 6, '', '', '', '', '', '', '', '']
      ]
      const instructionRows = [
        ['调研题目导入模板使用说明'],
        ['字段', '填写规则'],
        ['题目内容', '必填，员工在问卷中看到的完整问题'],
        ['题型', '必填：single_choice（单选）/ multiple_choice（多选）/ fill_blank（填空）'],
        ['是否必填', '填写“是”或“否”'],
        ['排序', '填写数字，数字越小越靠前'],
        ['最多选几项', '仅多选题填写数字；单选题和填空题留空'],
        ['选项1-选项7', '选择题至少填写 2 个选项；填空题留空'],
        ['导入位置', '请在“题目导入”工作表中填写或修改题目，不要改表头']
      ]
      const wb = XLSX.utils.book_new()
      const instructionWs = XLSX.utils.aoa_to_sheet(instructionRows)
      const questionWs = XLSX.utils.aoa_to_sheet(questionRows)
      XLSX.utils.book_append_sheet(wb, instructionWs, '使用说明')
      XLSX.utils.book_append_sheet(wb, questionWs, '题目导入')
      XLSX.writeFile(wb, '调研题目导入模板.xlsx')
    },
    async exportQuestions() {
      try {
        const params = this.taskId ? { task_id: this.taskId } : {}
        const res = await this.$api.exportSurveyQuestions(params)
        if (res.code === 200) {
          const csv = res.data.csv_content
          const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' })
          const link = document.createElement('a')
          link.href = URL.createObjectURL(blob)
          link.download = res.data.filename
          link.click()
        }
      } catch (e) {
        this.$message.error('导出失败')
      }
    },
    previewQuestion(row) {
      this.previewRow = row
      this.previewVisible = true
    },
    openSurveyPreview() {
      this.surveyPreviewVisible = true
    },
    openImport() {
      this.importVisible = true
      this.importContent = ''
      this.$nextTick(() => {
        if (this.$refs.uploadRef) this.$refs.uploadRef.clearFiles()
      })
    },
    handleFileChange(file) {
      if (!file.raw) return
      const fileName = (file.name || '').toLowerCase()
      const reader = new FileReader()
      reader.onload = (e) => {
        try {
          if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
            const data = new Uint8Array(e.target.result)
            const workbook = XLSX.read(data, { type: 'array' })
            const sheetName = workbook.SheetNames.includes('题目导入') ? '题目导入' : workbook.SheetNames[0]
            const sheet = workbook.Sheets[sheetName]
            const rows = XLSX.utils.sheet_to_json(sheet, { header: 1, defval: '' })
            this.importContent = this.rowsToCsv(rows)
          } else {
            this.importContent = e.target.result
          }
        } catch (err) {
          this.importContent = ''
          this.$message.error('文件解析失败：' + err.message)
        }
      }
      if (fileName.endsWith('.xlsx') || fileName.endsWith('.xls')) {
        reader.readAsArrayBuffer(file.raw)
      } else {
        reader.readAsText(file.raw)
      }
    },
    rowsToCsv(rows) {
      return rows
        .filter(row => row && row.some(cell => String(cell || '').trim()))
        .map(row => row.map(this.csvCell).join(','))
        .join('\n')
    },
    csvCell(value) {
      const text = String(value === null || value === undefined ? '' : value).trim()
      if (/[",\n\r]/.test(text)) {
        return `"${text.replace(/"/g, '""')}"`
      }
      return text
    },
    async confirmImport() {
      this.importing = true
      if (!this.importContent) {
        this.$message.warning('请先上传文件')
        this.importing = false
        return
      }
      try {
        const res = await this.$api.importSurveyQuestions({ csv_content: this.importContent, task_id: this.taskId })
        if (res.code === 200) {
          this.$message.success(`导入成功 ${res.data.imported_count} 条`)
          this.loadQuestions()
          this.importVisible = false
        }
      } catch (e) {
        this.$message.error('导入失败')
      } finally {
        this.importing = false
      }
    }
  }
}
</script>

<style scoped>
.page-container { max-width: 1200px; margin: 0 auto; }
.toolbar { display: flex; align-items: center; margin-bottom: var(--space-lg); flex-wrap: wrap; gap: 8px; }
.table-wrapper { background: var(--color-surface); border: 1px solid var(--color-border); border-radius: var(--radius-lg); overflow: hidden; box-shadow: var(--shadow-sm); }
.data-table >>> th { background: var(--color-bg) !important; color: var(--color-text) !important; font-weight: 600 !important; }
.pagination-bar { display: flex; justify-content: flex-end; padding: var(--space-md) var(--space-lg); background: var(--color-surface); border-top: 1px solid var(--color-border); }
.action-edit { color: var(--color-primary) !important; }
.action-disable { color: var(--color-high) !important; }

.option-row { display: flex; align-items: center; gap: 8px; margin-bottom: 8px; }

.import-area { padding: 0 10px; }
.import-tip { font-size: 13px; color: var(--color-text-secondary); margin-bottom: 16px; line-height: 1.6; }
.upload-box >>> .el-upload { width: 100%; }
.upload-box >>> .el-upload-dragger { width: 100%; }

/* 预览样式 */
.preview-content { padding: 10px; }
.preview-title {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 16px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.preview-number {
  width: 28px;
  height: 28px;
  border-radius: var(--radius-full);
  background: var(--color-primary-gradient);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}
.preview-required { color: var(--color-high); font-size: 16px; }
.preview-limit {
  margin-left: auto;
  font-size: 12px;
  color: var(--color-text-muted);
  background: var(--color-bg);
  padding: 2px 10px;
  border-radius: var(--radius-full);
}
.preview-type {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin-bottom: 16px;
}
.preview-options {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}
.preview-option {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  background: var(--color-bg);
  border-radius: var(--radius-md);
}
.preview-radio {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid var(--color-border);
  flex-shrink: 0;
}
.preview-checkbox {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 2px solid var(--color-border);
  flex-shrink: 0;
}
.preview-fill { padding: var(--space-md); }
.preview-fill-placeholder {
  height: 80px;
  background: var(--color-bg);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}

/* 问卷整体预览样式 */
.survey-preview {
  max-height: 520px;
  overflow-y: auto;
  padding-right: 8px;
}
.survey-preview-header {
  text-align: center;
  padding-bottom: var(--space-lg);
  margin-bottom: var(--space-lg);
  border-bottom: 1px solid var(--color-border);
}
.survey-preview-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--color-text);
  margin: 0 0 8px 0;
}
.survey-preview-subtitle {
  font-size: 13px;
  color: var(--color-text-secondary);
  margin: 0;
}
.survey-preview-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-lg);
}
.survey-preview-item {
  background: var(--color-bg);
  border-radius: var(--radius-lg);
  padding: var(--space-md) var(--space-lg);
}
.survey-preview-question {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 15px;
  font-weight: 600;
  color: var(--color-text);
  margin-bottom: 12px;
  flex-wrap: wrap;
}
.survey-preview-number {
  width: 26px;
  height: 26px;
  border-radius: var(--radius-full);
  background: var(--color-primary-gradient);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  flex-shrink: 0;
}
.survey-preview-required { color: var(--color-high); font-size: 16px; }
.survey-preview-limit {
  margin-left: auto;
  font-size: 12px;
  color: var(--color-text-muted);
  background: var(--color-surface);
  padding: 2px 10px;
  border-radius: var(--radius-full);
  font-weight: normal;
}
.survey-preview-options {
  display: flex;
  flex-direction: column;
  gap: var(--space-sm);
}
.survey-preview-option {
  display: flex;
  align-items: center;
  gap: var(--space-sm);
  padding: var(--space-sm) var(--space-md);
  background: var(--color-surface);
  border-radius: var(--radius-md);
  font-size: 14px;
}
.survey-preview-radio {
  width: 14px;
  height: 14px;
  border-radius: 50%;
  border: 2px solid var(--color-border);
  flex-shrink: 0;
}
.survey-preview-checkbox {
  width: 14px;
  height: 14px;
  border-radius: 3px;
  border: 2px solid var(--color-border);
  flex-shrink: 0;
}
.survey-preview-fill { padding: var(--space-md) 0; }
.survey-preview-fill-placeholder {
  height: 60px;
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-md);
}
</style>
